import { AlertTriangle, Zap, ShieldAlert, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

export const QuarantinePanel = ({ quarantine, recovering, pendingFault, onTrigger, domain, lastSealed, onReset }) => {
  return (
    <div
      className={`rounded-2xl p-5 h-full flex flex-col border transition-colors ${
        quarantine
          ? "border-red-600/70 bg-red-950/30 quarantine-flash"
          : "glass-panel"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="mono text-xs font-semibold uppercase tracking-widest text-cyan-400">
          Fault Injection & Interlock
        </h3>
        <ShieldAlert
          className={`h-4 w-4 ${quarantine ? "text-red-400" : "text-slate-500"}`}
        />
      </div>

      {quarantine ? (
        <div className="flex-1 flex flex-col gap-3 overflow-y-auto">
          <div
            data-testid="quarantine-alert-banner"
            className="rounded-xl border-2 border-red-600 bg-red-950/60 p-4 text-center shadow-[0_0_30px_rgba(239,68,68,0.45)]"
          >
            <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
            <p className="mono text-sm font-extrabold tracking-widest text-red-200">
              COMMAND QUARANTINED
            </p>
            <p className="mono text-[11px] text-red-300/80 mt-2 leading-relaxed">
              Governor intercepted a Lyapunov violation. Telemetry frozen to the
              laminar safe-holding profile. Awaiting human correction.
            </p>
            <div className="mt-2 mono text-[10px] text-red-400/70">
              {pendingFault?.faultType} · V(x)={pendingFault?.spikeValue}
            </div>
          </div>

          {pendingFault?.diagnosis && (
            <div
              data-testid="cross-coupling-panel"
              className="rounded-xl border border-amber-700/50 bg-amber-950/15 p-4 text-left"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="mono text-[10px] uppercase tracking-widest text-amber-300">
                  Cross-Coupling Diagnosis
                </p>
                <span className="mono text-[10px] text-amber-300/90">
                  coupled index {pendingFault.diagnosis.coupledRate} / envelope{" "}
                  {pendingFault.diagnosis.envelope.toFixed(2)}
                </span>
              </div>
              <p className="mono text-[11px] text-slate-200 mb-2">
                Interacting pair · {pendingFault.diagnosis.pair}
              </p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {pendingFault.diagnosis.rates.map((r) => (
                  <div key={r.sym} className="rounded-md border border-slate-800 bg-slate-950/50 px-2.5 py-1.5">
                    <span className="mono text-[10px] text-slate-400">{r.sym} = </span>
                    <span className="mono text-[11px] text-amber-200 font-semibold">{r.value}</span>
                    <span className="mono text-[9px] text-slate-500"> {r.unit}</span>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-300 leading-relaxed">
                {pendingFault.diagnosis.message}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col justify-center">
          <p className="text-xs text-slate-400 leading-relaxed mb-4">
            Inject an unsafe actuator command that drives{" "}
            <span className="mono text-cyan-300">V(x)</span> above the stability
            threshold. The deterministic governor will intercept it before it
            reaches the <span className="text-slate-200">{domain.short}</span>{" "}
            process.
          </p>
          <Button
            data-testid="trigger-fault-button"
            onClick={onTrigger}
            disabled={recovering}
            className="w-full h-12 mono font-bold tracking-widest bg-red-600 hover:bg-red-500 text-white border border-red-500/50 shadow-[0_0_20px_-4px_rgba(239,68,68,0.6)] transition-all hover:shadow-[0_0_26px_-2px_rgba(239,68,68,0.8)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Zap className="h-4 w-4 mr-2" />
            {recovering ? "RECOVERY IN PROGRESS…" : "TRIGGER LYAPUNOV FAULT"}
          </Button>
          {lastSealed && !recovering && (
            <>
              <Button
                data-testid="reset-simulation-button"
                onClick={onReset}
                variant="outline"
                className="w-full h-11 mt-3 mono font-bold tracking-widest border-slate-700 bg-slate-900/40 text-slate-200 hover:border-cyan-600/60 hover:text-cyan-300 transition-all"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                RESET SIMULATION
              </Button>
              <p className="mt-2 mono text-[10px] text-slate-500 text-center">
                Returns telemetry & governor to nominal · audit log preserved
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
